(function () {
  const SELECTOR = '[data-flippo-plan-calculator]';
  const ACTIVITY_VALUES = new Set(['tranquilo', 'normal', 'activo']);
  const STORAGE_KEY = 'flippo_plan_v1';

  if (window.FlippoPlanCalculator) {
    window.FlippoPlanCalculator.initializeAll(document);
    return;
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-fpc-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Flippo plan calculator: invalid configuration', error);
      return {};
    }
  }

  function clampInteger(value, min, max, fallback) {
    const number = Number.parseInt(value, 10);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }

  function readSavedState() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeSavedState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage can be unavailable in privacy modes; the form remains functional.
    }
  }

  function createResponseId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `fpc-${window.crypto.randomUUID()}`;
    }
    const random = Math.random().toString(36).slice(2, 12);
    return `fpc-${Date.now().toString(36)}-${random}`;
  }

  function replaceName(template, name) {
    return String(template || '').replace(/\[nombre\]/gi, name);
  }

  function sizeLabel(weight, config) {
    if (weight <= 2) return config.sizeMini || 'Talla mini';
    if (weight <= 9) return config.sizeSmall || 'Talla pequeña';
    if (weight <= 25) return config.sizeMedium || 'Talla mediana';
    return config.sizeLarge || 'Talla grande';
  }

  function ageText(months) {
    if (months < 12) return `${months} ${months === 1 ? 'mes' : 'meses'}`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const yearsText = `${years} ${years === 1 ? 'año' : 'años'}`;
    if (!remainingMonths) return yearsText;
    return `${yearsText} y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
  }

  function lifeStage(months, config) {
    if (months < 12) {
      return { key: 'cachorro', label: config.stagePuppy || 'Cachorro' };
    }
    if (months >= 96) {
      return { key: 'senior', label: config.stageSenior || 'Senior' };
    }
    return { key: 'adulto', label: config.stageAdult || 'Adulto' };
  }

  function updateRangeFill(input) {
    const min = Number(input.min);
    const max = Number(input.max);
    const value = Number(input.value);
    const percentage = ((value - min) / (max - min)) * 100;
    input.style.setProperty('--fill', `${percentage}%`);
  }

  function buildWaitUrl(waitUrl, responseId) {
    const target = new URL(waitUrl, window.location.origin);
    target.searchParams.set('responseId', responseId);

    const passthroughKeys = [
      'gclid',
      'gclsrc',
      'gbraid',
      'wbraid',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'ref',
      'promo'
    ];
    const currentParams = new URLSearchParams(window.location.search);
    passthroughKeys.forEach((key) => {
      const value = currentParams.get(key);
      if (value && !target.searchParams.has(key)) target.searchParams.set(key, value);
    });

    return target.toString();
  }

  function localDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function initialize(root) {
    if (!root || root.dataset.fpcInitialized === 'true') return;
    root.dataset.fpcInitialized = 'true';

    const config = parseConfig(root);
    const form = root.querySelector('[data-fpc-form]');
    const nameInput = root.querySelector('[data-fpc-name]');
    const weightInput = root.querySelector('[data-fpc-weight]');
    const ageInput = root.querySelector('[data-fpc-age]');
    const activityGroup = root.querySelector('[data-fpc-activity]');
    const title = root.querySelector('[data-fpc-title]');
    const weightValue = root.querySelector('[data-fpc-weight-value]');
    const size = root.querySelector('[data-fpc-size]');
    const ageValue = root.querySelector('[data-fpc-age-value]');
    const stage = root.querySelector('[data-fpc-stage]');
    const submit = root.querySelector('[data-fpc-submit]');
    const submitLabel = root.querySelector('[data-fpc-submit-label]');
    const errorNode = root.querySelector('[data-fpc-error]');
    const petStep = root.querySelector('[data-fpc-pet-step]');
    const emailStep = root.querySelector('[data-fpc-email-step]');
    const emailInput = root.querySelector('[data-fpc-email]');
    const marketingInput = root.querySelector('[data-fpc-marketing]');
    const backButton = root.querySelector('[data-fpc-back]');
    const captureEmail = config.captureEmail === true && Boolean(emailStep && emailInput && marketingInput);
    let emailStepVisible = false;
    let submissionKey = '';

    if (!form || !nameInput || !weightInput || !ageInput || !activityGroup || !submit) return;

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const saved = designMode ? {} : readSavedState();
    const defaultActivity = ACTIVITY_VALUES.has(config.defaultActivity)
      ? config.defaultActivity
      : 'normal';
    const state = {
      name: typeof saved.name === 'string' ? saved.name.slice(0, 50) : '',
      kg: clampInteger(saved.kg, 1, 40, clampInteger(weightInput.value, 1, 40, 12)),
      ageMonths: clampInteger(
        saved.ageMonths || saved.ageM,
        2,
        192,
        clampInteger(ageInput.value, 2, 192, 48)
      ),
      activity: ACTIVITY_VALUES.has(saved.activity) ? saved.activity : defaultActivity
    };
    let responseId = '';

    nameInput.value = state.name;
    weightInput.value = String(state.kg);
    ageInput.value = String(state.ageMonths);

    function setError(message) {
      if (!errorNode) return;
      errorNode.textContent = message || '';
      errorNode.hidden = !message;
    }

    function render() {
      const name = state.name.trim();
      if (title) {
        title.textContent = name
          ? replaceName(config.namedTitle || 'El plan de [nombre]', name)
          : config.formTitle || 'El plan de tu perro';
      }

      if (weightValue) weightValue.textContent = `${state.kg} kg`;
      if (size) size.textContent = sizeLabel(state.kg, config);
      weightInput.setAttribute('aria-valuetext', `${state.kg} kg, ${sizeLabel(state.kg, config)}`);
      updateRangeFill(weightInput);

      const readableAge = ageText(state.ageMonths);
      const currentStage = lifeStage(state.ageMonths, config);
      if (ageValue) ageValue.textContent = readableAge;
      ageInput.setAttribute('aria-valuetext', `${readableAge}, ${currentStage.label}`);
      updateRangeFill(ageInput);
      if (stage) {
        stage.textContent = currentStage.label;
        stage.dataset.stage = currentStage.key;
      }

      activityGroup.querySelectorAll('[data-activity]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.activity === state.activity ? 'true' : 'false');
      });

      if (submitLabel && !submit.disabled) {
        submitLabel.textContent = emailStepVisible ? config.emailCta || 'Ver su plan' : name
          ? replaceName(config.namedCta || 'Crear el plan de [nombre]', name)
          : config.ctaLabel || 'Empezar con su plan';
      }

      if (!designMode) {
        writeSavedState({
          name: state.name,
          kg: state.kg,
          ageM: state.ageMonths,
          ageMonths: state.ageMonths,
          activity: state.activity
        });
      }
    }

    nameInput.addEventListener('input', () => {
      state.name = nameInput.value;
      nameInput.removeAttribute('aria-invalid');
      setError('');
      render();
    });

    weightInput.addEventListener('input', () => {
      state.kg = clampInteger(weightInput.value, 1, 40, state.kg);
      render();
    });

    ageInput.addEventListener('input', () => {
      state.ageMonths = clampInteger(ageInput.value, 2, 192, state.ageMonths);
      render();
    });

    activityGroup.querySelectorAll('[data-activity]').forEach((button) => {
      button.addEventListener('click', () => {
        const activity = button.dataset.activity;
        if (!ACTIVITY_VALUES.has(activity)) return;
        state.activity = activity;
        render();
      });
    });

    if (backButton) backButton.addEventListener('click', () => {
      if (submit.disabled) return;
      emailStepVisible = false;
      emailStep.hidden = true;
      emailInput.disabled = true;
      marketingInput.disabled = true;
      petStep.hidden = false;
      setError('');
      render();
      nameInput.focus();
    });
    if (emailInput) emailInput.addEventListener('input', () => {
      emailInput.removeAttribute('aria-invalid');
      setError('');
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submit.disabled) return;

      state.name = nameInput.value.slice(0, 50);
      const petName = state.name.trim();
      if (!petName) {
        nameInput.setAttribute('aria-invalid', 'true');
        setError(config.nameError || 'Escribe el nombre de tu perro para continuar.');
        nameInput.focus();
        return;
      }

      if (captureEmail && !emailStepVisible) {
        emailStepVisible = true;
        petStep.hidden = true;
        emailStep.hidden = false;
        emailInput.disabled = false;
        marketingInput.disabled = false;
        setError('');
        render();
        emailInput.focus();
        return;
      }
      if (captureEmail) {
        emailInput.value = emailInput.value.trim();
        if (!emailInput.checkValidity()) {
          emailInput.setAttribute('aria-invalid', 'true');
          setError(config.emailError || 'Introduce un email válido para continuar.');
          emailInput.focus();
          return;
        }
      }

      const endpointUrl = String(config.endpointUrl || '').trim();
      const waitUrl = String(config.waitUrl || '').trim();
      if (!endpointUrl || !waitUrl) {
        setError(config.requestError || 'No hemos podido crear el plan. Inténtalo de nuevo.');
        return;
      }

      const nextSubmissionKey = JSON.stringify([state, captureEmail ? emailInput.value : '', captureEmail && marketingInput.checked]);
      if (nextSubmissionKey !== submissionKey) responseId = createResponseId();
      submissionKey = nextSubmissionKey;
      setError('');
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      if (submitLabel) submitLabel.textContent = config.loadingLabel || 'Creando su plan…';

      const submittedAt = new Date();
      const payload = {
        source: 'flippo-plan-calculator',
        responseId,
        frontendId: responseId,
        name: petName,
        kg: state.kg,
        ageMonths: state.ageMonths,
        activity: state.activity,
        referenceDate: localDate(submittedAt),
        createdAt: submittedAt.toISOString(),
        pageUrl: window.location.href
      };
      if (captureEmail) {
        payload.captureEmail = true;
        payload.email = emailInput.value;
        payload.acceptsMarketing = marketingInput.checked;
      }

      try {
        const response = await fetch(endpointUrl, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Quiz request failed');
        }
        if (captureEmail && data.contactSaved !== true) {
          throw new Error('The connector has not confirmed the contact was saved');
        }

        const finalResponseId = data.responseId || responseId;
        try {
          window.localStorage.setItem(
            'flippo_quiz',
            JSON.stringify({
              name: petName,
              kg: state.kg,
              ageMonths: state.ageMonths,
              stage: lifeStage(state.ageMonths, config).key,
              activity: state.activity,
              responseId: finalResponseId,
              ts: Date.now()
            })
          );
        } catch (error) {
          // The result flow does not depend on local storage.
        }

        const redirectUrl = buildWaitUrl(data.redirectUrl || waitUrl, finalResponseId);
        window.location.assign(redirectUrl);
      } catch (error) {
        console.error('Flippo plan calculator: request failed', error);
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        setError(config.requestError || 'No hemos podido crear el plan. Revisa tu conexión e inténtalo de nuevo.');
        render();
      }
    });

    render();
  }

  function initializeAll(container) {
    if (container.matches && container.matches(SELECTOR)) initialize(container);
    container.querySelectorAll(SELECTOR).forEach(initialize);
  }

  window.FlippoPlanCalculator = { initializeAll };

  initializeAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
